import { useEffect, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/dates";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, RotateCcw, History, Lock, FileDown, Mail, DollarSign, Download, AlertTriangle, Save, RefreshCw } from "lucide-react";

const money = (v) => `$${(Number(v) || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const LOCKED = ["approved", "payroll_ready", "exported", "paid", "summary_sent"];
const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-600", awaiting_review: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700", payroll_ready: "bg-indigo-100 text-indigo-700",
  summary_sent: "bg-teal-100 text-teal-700", exported: "bg-blue-100 text-blue-700", paid: "bg-green-100 text-green-700",
};
const STATUS_LABEL = { draft: "Draft", awaiting_review: "Awaiting Review", approved: "Approved",
  payroll_ready: "Ready for Payroll", summary_sent: "Summary Sent", exported: "Exported", paid: "Paid" };
const PAY_METHODS = ["hourly", "metre", "combined"];

function fetchBlob(path, filename) {
  const token = localStorage.getItem("ts_token");
  return fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob())
    .then((b) => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    });
}

function NumCell({ value, onChange, disabled, testid }) {
  const [v, setV] = useState(value ?? 0);
  useEffect(() => setV(value ?? 0), [value]);
  return (
    <input type="number" step="0.25" value={v} disabled={disabled} data-testid={testid}
      onChange={(e) => { setV(e.target.value); onChange && onChange(parseFloat(e.target.value) || 0); }}
      onBlur={() => { const parsed = parseFloat(v) || 0; setV(parsed); onChange && onChange(parsed); }}
      className="w-16 px-2 py-1 rounded border border-slate-200 text-right text-sm disabled:bg-slate-50 disabled:text-slate-400" />
  );
}

function TimesheetDetail({ tid, onClose, onChanged }) {
  const [ts, setTs] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [emailConfirm, setEmailConfirm] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [pending, setPending] = useState({});   // { lineId: { field: value, ... }, ... }
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => { api.get(`/timesheets/${tid}`).then((r) => { setTs(r.data); setPending({}); }); }, [tid]);
  useEffect(() => { load(); }, [load]);
  if (!ts) return null;
  const locked = LOCKED.includes(ts.status);
  const hasPending = Object.keys(pending).length > 0;

  const trackChange = (lineId, field, value) => {
    setPending((prev) => {
      const linePatches = { ...(prev[lineId] || {}), [field]: value };
      // Check if the value matches server — if so, remove the patch
      const serverLine = ts.lines.find((l) => l.line_id === lineId);
      if (serverLine && String(serverLine[field] ?? 0) === String(value)) {
        delete linePatches[field];
      }
      if (Object.keys(linePatches).length === 0) {
        const next = { ...prev };
        delete next[lineId];
        return next;
      }
      return { ...prev, [lineId]: linePatches };
    });
  };

  const saveAll = async () => {
    const updates = Object.entries(pending).map(([line_id, patch]) => ({ line_id, ...patch }));
    if (updates.length === 0) return;
    setSaving(true);
    try {
      const r = await api.patch(`/timesheets/${tid}/lines`, { updates });
      setTs(r.data);
      setPending({});
      onChanged && onChanged();
      toast.success("Timesheet saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveLine = (lineId, patch) => {
    api.patch(`/timesheets/${tid}/line/${lineId}`, patch)
      .then((r) => { setTs(r.data); setPending({}); onChanged && onChanged(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Update failed"));
  };
  const approve = () => api.post(`/timesheets/${tid}/approve`).then((r) => { setTs(r.data); onChanged && onChanged(); toast.success("Week approved"); });
  const reopen = () => api.post(`/timesheets/${tid}/reopen`).then((r) => { setTs(r.data); onChanged && onChanged(); toast.success("Week reopened"); });
  const emailSummary = () => {
    setEmailing(true);
    api.post(`/timesheets/${tid}/email-summary`)
      .then((r) => {
        load(); onChanged && onChanged();
        const status = r.data.delivery_status;
        if (status === "sent") toast.success(`Summary emailed to ${r.data.recipient}`);
        else toast.warning(`Summary not delivered (${status}). Please check email settings.`);
        setEmailConfirm(false);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Email failed"))
      .finally(() => setEmailing(false));
  };
  const setStatus = (status) => api.post(`/timesheets/${tid}/status`, { status }).then((r) => { setTs(r.data); onChanged && onChanged(); toast.success("Status updated"); });
  const isContractor = ts.worker_type === "contractor";
  const payHold = ts.pay_hold && ts.pay_hold.length > 0;

  const t = ts.totals || {};
  return (
    <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="timesheet-detail-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {ts.worker_name}
          <span className="text-xs font-normal text-slate-400">· {ts.worker_type} · Week {formatDate(ts.week_start)}–{formatDate(ts.week_end)}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_TONE[ts.status]}`}>{STATUS_LABEL[ts.status]}</span>
        </DialogTitle>
        <DialogDescription>Review and approve daily lines, correct hours/metres, approve overtime &amp; travel, and add adjustments.</DialogDescription>
      </DialogHeader>

      {locked && <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2"><Lock className="w-3.5 h-3.5" />Week is {ts.status}. Reopen to edit.</div>}

      {payHold && (
        <div data-testid="ts-pay-hold-banner" className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
          <div><b>Pay on hold.</b> {ts.pay_hold.join("; ")}. Payment / summary is blocked until resolved.</div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-slate-500 uppercase tracking-wider">
              <th className="text-left px-2 py-2">Date</th>
              <th className="text-left px-2 py-2">Job / Site</th>
              <th className="px-1 py-2">Std</th><th className="px-1 py-2">Sat</th>
              <th className="px-1 py-2">OT</th><th className="px-1 py-2">Travel</th>
              <th className="px-1 py-2">Metres</th><th className="px-1 py-2">Method</th>
              <th className="px-1 py-2">Adj $</th><th className="text-right px-2 py-2">Daily</th>
              <th className="px-1 py-2">✓</th>
            </tr>
          </thead>
          <tbody>
            {ts.lines.map((l) => (
              <tr key={l.line_id} className="border-t border-slate-100" data-testid={`ts-line-${l.line_id}`}>
                <td className="px-2 py-2 whitespace-nowrap"><span className="font-medium text-navy">{l.day.slice(0, 3)}</span> {formatDate(l.date)}</td>
                <td className="px-2 py-2"><p className="font-medium text-navy">{l.job_number}</p><p className="text-slate-400 max-w-[160px] truncate">{l.site_address}</p></td>
                <td className="px-1 py-2 text-center"><NumCell value={(pending[l.line_id] || {}).standard_hours ?? l.standard_hours} disabled={locked} testid={`ts-line-std-${l.line_id}`} onChange={(v) => trackChange(l.line_id, "standard_hours", v)} /></td>
                <td className="px-1 py-2 text-center"><NumCell value={(pending[l.line_id] || {}).saturday_hours ?? l.saturday_hours} disabled={locked} testid={`ts-line-sat-${l.line_id}`} onChange={(v) => trackChange(l.line_id, "saturday_hours", v)} /></td>
                <td className="px-1 py-2 text-center"><NumCell value={(pending[l.line_id] || {}).overtime_hours ?? l.overtime_hours} disabled={locked} testid={`ts-line-ot-${l.line_id}`} onChange={(v) => trackChange(l.line_id, "overtime_hours", v)} /></td>
                <td className="px-1 py-2 text-center"><NumCell value={(pending[l.line_id] || {}).travel_hours ?? l.travel_hours} disabled={locked} testid={`ts-line-travel-${l.line_id}`} onChange={(v) => trackChange(l.line_id, "travel_hours", v)} /></td>
                <td className="px-1 py-2 text-center"><NumCell value={(pending[l.line_id] || {}).metres ?? l.metres} disabled={locked} testid={`ts-line-metres-${l.line_id}`} onChange={(v) => trackChange(l.line_id, "metres", v)} /></td>
                <td className="px-1 py-2 text-center">
                  <select value={(pending[l.line_id] || {}).payment_method ?? l.payment_method} disabled={locked} data-testid={`ts-line-method-${l.line_id}`} onChange={(e) => trackChange(l.line_id, "payment_method", e.target.value)}
                    className="px-1 py-1 rounded border border-slate-200 text-xs capitalize disabled:bg-slate-50">
                    {PAY_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </td>
                <td className="px-1 py-2 text-center"><NumCell value={(pending[l.line_id] || {}).adjustment_amount ?? l.adjustment_amount} disabled={locked} testid={`ts-line-adj-${l.line_id}`} onChange={(v) => trackChange(l.line_id, "adjustment_amount", v)} /></td>
                <td className="text-right px-2 py-2 font-semibold text-navy whitespace-nowrap">{money(l.daily_total)}</td>
                <td className="px-1 py-2 text-center">
                  <input type="checkbox" checked={!!l.approved} disabled={locked} onChange={(e) => saveLine(l.line_id, { approved: e.target.checked })} data-testid={`ts-line-approve-${l.line_id}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-slate-50 rounded-xl p-4 grid grid-cols-3 md:grid-cols-6 gap-3 text-sm">
        <div><p className="text-xs text-slate-400">Std Hrs</p><p className="font-bold text-navy">{t.standard_hours}</p></div>
        <div><p className="text-xs text-slate-400">Sat Hrs</p><p className="font-bold text-navy">{t.saturday_hours}</p></div>
        <div><p className="text-xs text-slate-400">OT Hrs</p><p className="font-bold text-navy">{t.overtime_hours}</p></div>
        <div><p className="text-xs text-slate-400">Travel Hrs</p><p className="font-bold text-navy">{t.travel_hours}</p></div>
        <div><p className="text-xs text-slate-400">Metres</p><p className="font-bold text-navy">{t.metres}</p></div>
        <div className="rounded-lg bg-emerald-50 px-2 py-1"><p className="text-xs text-emerald-600">Weekly Total</p><p className="font-bold text-emerald-700" data-testid="ts-detail-total">{money(t.weekly_total)}</p></div>
      </div>

      {hasPending && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
          <span className="text-sm text-amber-800 font-medium">You have unsaved changes</span>
          <button onClick={saveAll} disabled={saving} data-testid="ts-save-changes-btn"
            className="ml-auto flex items-center gap-1.5 px-5 py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-light disabled:opacity-50">
            <Save className="w-4 h-4" />{saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <button onClick={() => setShowAudit((s) => !s)} className="text-xs text-slate-500 flex items-center gap-1 hover:text-navy"><History className="w-3.5 h-3.5" />Audit History ({(ts.audit_log || []).length})</button>
        <div className="flex flex-wrap gap-2">
          {isContractor && (locked || ts.status === "approved") && (
            <>
              <button onClick={() => fetchBlob(`/timesheets/${tid}/summary-pdf`, `Payment-Summary-${ts.week_end}.pdf`)} data-testid="ts-summary-pdf-btn" className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-navy hover:bg-slate-50"><FileDown className="w-4 h-4" />Summary PDF</button>
              <button onClick={() => setEmailConfirm(true)} disabled={payHold} title={payHold ? ts.pay_hold_reason : ""} data-testid="ts-email-summary-btn" className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white ${payHold ? "bg-slate-300 cursor-not-allowed" : "bg-teal-600 hover:bg-teal-700"}`}><Mail className="w-4 h-4" />Email Summary</button>
            </>
          )}
          {!isContractor && ts.status === "approved" && (
            <button onClick={() => setStatus("payroll_ready")} disabled={payHold} title={payHold ? ts.pay_hold_reason : ""} data-testid="ts-payroll-btn" className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white ${payHold ? "bg-slate-300 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"}`}><DollarSign className="w-4 h-4" />Ready for Payroll</button>
          )}
          {!isContractor && ts.status === "payroll_ready" && (
            <button onClick={() => setStatus("paid")} disabled={payHold} title={payHold ? ts.pay_hold_reason : ""} data-testid="ts-paid-btn" className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white ${payHold ? "bg-slate-300 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}><CheckCircle2 className="w-4 h-4" />Mark Paid</button>
          )}
          {locked ? (
            <button onClick={reopen} data-testid="ts-reopen-btn" className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-navy hover:bg-slate-50"><RotateCcw className="w-4 h-4" />Reopen Week</button>
          ) : (
            <button onClick={approve} data-testid="ts-approve-btn" className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4" />Approve Week</button>
          )}
        </div>
      </div>

      <div data-testid="ts-audit-log" className={showAudit ? "border border-slate-100 rounded-xl p-3 max-h-52 overflow-y-auto" : "hidden"}>
        {(ts.audit_log || []).slice().reverse().map((a, i) => (
          <div key={i} className="text-xs py-1.5 border-b border-slate-50 last:border-0">
            <span className="text-slate-400">{formatDateTime(a.at)}</span> · <span className="font-medium text-navy">{a.user}</span> · {a.action}
            {a.old !== "" && <span className="text-slate-400"> ({a.old} → {a.new})</span>}
          </div>
        ))}
      </div>

      {(ts.summary_history || []).length > 0 && (
        <div className="border border-slate-100 rounded-xl p-3" data-testid="ts-summary-history">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Payment Summary Email History</p>
          {ts.summary_history.slice().reverse().map((s, i) => (
            <div key={i} className="text-xs py-1 flex items-center justify-between">
              <span className="text-slate-500">{formatDateTime(s.email_sent_at || s.pdf_generated_at)} → {s.recipient}</span>
              <span className={`px-2 py-0.5 rounded-full font-semibold ${s.delivery_status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{s.delivery_status}</span>
            </div>
          ))}
        </div>
      )}

      <Dialog open={emailConfirm} onOpenChange={(o) => !emailing && setEmailConfirm(o)}>
        <DialogContent className="max-w-md" data-testid="ts-email-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Email payment summary</DialogTitle>
            <DialogDescription>
              A PDF payment summary for <b>{ts.worker_name}</b> (week ending {formatDate(ts.week_end)}) will be emailed to their address on file. A copy is also BCC'd to the office.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setEmailConfirm(false)} disabled={emailing} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-navy hover:bg-slate-50 disabled:opacity-50">Cancel</button>
            <button onClick={emailSummary} disabled={emailing} data-testid="ts-email-confirm-send" className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50"><Mail className="w-4 h-4" />{emailing ? "Sending…" : "Send Summary"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </DialogContent>
  );
}

export default function Timesheets() {
  const [data, setData] = useState({ timesheets: [], weeks: [] });
  const [week, setWeek] = useState("");
  const [wtype, setWtype] = useState("");
  const [status, setStatus] = useState("");
  const [openId, setOpenId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (week) p.set("week", week);
    if (wtype) p.set("worker_type", wtype);
    if (status) p.set("status", status);
    api.get(`/timesheets?${p.toString()}`).then((r) => setData(r.data));
  }, [week, wtype, status]);
  useEffect(() => { load(); }, [load]);

  const handleRefresh = () => {
    setRefreshing(true);
    api.post("/timesheets/generate")
      .then(() => { load(); toast.success("Timesheets refreshed from jobs"); })
      .catch(() => toast.error("Refresh failed"))
      .finally(() => setRefreshing(false));
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="admin-timesheets-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">Timesheets</h1>
        <p className="text-slate-500 mt-1">Company timesheets &amp; payroll-cost view · auto-generated from approved jobs</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <select data-testid="ts-filter-week" value={week} onChange={(e) => setWeek(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
          <option value="">All Weeks</option>
          {data.weeks.map((w) => <option key={w} value={w}>{`Week of ${formatDate(w)}`}</option>)}
        </select>
        <select data-testid="ts-filter-type" value={wtype} onChange={(e) => setWtype(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
          <option value="">All Workers</option>
          <option value="employee">Employees</option>
          <option value="contractor">Contractors</option>
        </select>
        <select data-testid="ts-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="awaiting_review">Awaiting Approval</option>
          <option value="approved">Approved</option>
          <option value="payroll_ready">Ready for Payroll</option>
          <option value="summary_sent">Summary Sent</option>
        </select>
        <button data-testid="ts-refresh-btn" onClick={handleRefresh} disabled={refreshing}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-navy hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />{refreshing ? "Refreshing…" : "Refresh from Jobs"}
        </button>
        <button data-testid="ts-export-btn" onClick={() => fetchBlob(`/timesheets-export?worker_type=employee${week ? `&week=${week}` : ""}`, "payroll-hours.xlsx")}
          className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-navy hover:bg-slate-50">
          <Download className="w-4 h-4" />Export Payroll Hours
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-3 py-3 font-semibold">Type</th>
                <th className="text-left px-3 py-3 font-semibold">Week Ending</th>
                <th className="text-right px-2 py-3 font-semibold">Std</th>
                <th className="text-right px-2 py-3 font-semibold">Sat</th>
                <th className="text-right px-2 py-3 font-semibold">OT</th>
                <th className="text-right px-2 py-3 font-semibold">Travel</th>
                <th className="text-right px-2 py-3 font-semibold">Metres</th>
                <th className="text-right px-2 py-3 font-semibold">Adj</th>
                <th className="text-right px-3 py-3 font-semibold">Weekly Total</th>
                <th className="text-center px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.timesheets.length === 0 && <tr><td colSpan={11} className="text-center text-slate-400 py-10">No timesheets. They generate automatically when jobs are moved to Ready to Bill.</td></tr>}
              {data.timesheets.map((ts) => {
                const t = ts.totals || {};
                return (
                  <tr key={ts.id} data-testid={`ts-row-${ts.id}`} onClick={() => setOpenId(ts.id)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="px-4 py-3 font-semibold text-navy">{ts.worker_name}</td>
                    <td className="px-3 py-3 capitalize text-slate-500">{ts.worker_type}</td>
                    <td className="px-3 py-3 text-slate-600">{formatDate(ts.week_end)}</td>
                    <td className="text-right px-2 py-3">{t.standard_hours || 0}</td>
                    <td className="text-right px-2 py-3">{t.saturday_hours || 0}</td>
                    <td className="text-right px-2 py-3">{t.overtime_hours || 0}</td>
                    <td className="text-right px-2 py-3">{t.travel_hours || 0}</td>
                    <td className="text-right px-2 py-3">{t.metres || 0}</td>
                    <td className="text-right px-2 py-3">{money(t.adjustments)}</td>
                    <td className="text-right px-3 py-3 font-semibold text-navy">{money(t.weekly_total)}</td>
                    <td className="text-center px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_TONE[ts.status] || STATUS_TONE.draft}`}>{STATUS_LABEL[ts.status] || ts.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        {openId && <TimesheetDetail tid={openId} onClose={() => setOpenId(null)} onChanged={load} />}
      </Dialog>
    </div>
  );
}
